import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Alert,
  Card,
} from "react-bootstrap";

import DOMPurify from "dompurify";
import AppNavbar from "../../components/common/AppNavbar";

const FoodDetails = () => {
  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // dish object passed from AncientFoods.jsx
  const dishFromState = location.state?.dish;

  const [foodDetails, setFoodDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sanitizedPrep, setSanitizedPrep] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await fetch(
          "https://www.bharatabroad.com/api/ai/food-details",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
          }
        );

        if (!res.ok) throw new Error("Failed to fetch AI details");

        const data = await res.json();
        setFoodDetails(data);

        const safePrep = DOMPurify.sanitize(data.preparation, {
          ALLOWED_TAGS: ["p", "ul", "li", "b", "i", "h3", "h4", "img"],
          ALLOWED_ATTR: ["src", "alt", "style"],
        });
        setSanitizedPrep(safePrep);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [name]);

  // choose image & description priority: state -> API
  const dishImage = dishFromState?.image || foodDetails?.image;
  const dishDescription =
    dishFromState?.description || foodDetails?.description;

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p>Generating details for {name}...</p>
      </div>
    );

  if (error)
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );

  if (!foodDetails)
    return (
      <Container className="py-5 text-center text-danger">
        Error: No details found.
        <Button className="ms-2" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );

  return (
    <>
      <AppNavbar />
      <Container fluid className="mt-4">
        <Row>
          {/* Main Content */}
          <Col lg={9}>
            <h2 className="fw-bold">{name}</h2>
            {dishDescription && <p className="text-muted">{dishDescription}</p>}
            <hr />

            {/* History */}
            {foodDetails.history && (
              <Card className="border-0 mb-4">
                <Card.Body>
                  <Card.Title>History</Card.Title>
                  <Card.Text>{foodDetails.history}</Card.Text>
                </Card.Body>
              </Card>
            )}

            {/* Dish Image + Ingredients side by side */}
            {(dishImage || foodDetails.ingredients?.length > 0) && (
              <Row className="p-2 align-items-start mb-1">
                {/* Left: Dish Image */}
                <Col xs={12} md={6} className="mb-3 mb-md-0 text-center">
                  {dishImage && (
                    <img
                      src={dishImage}
                      alt={name}
                      className="img-fluid rounded shadow-sm"
                      style={{
                        maxHeight: "400px",
                        objectFit: "cover",
                        width: "100%",
                      }}
                    />
                  )}
                </Col>

                {/* Right: Ingredients */}
                <Col xs={12} md={6}>
                  {foodDetails.ingredients?.length > 0 && (
                    <Card className="border-0 h-100">
                      <Card.Body>
                        <Card.Title>Ingredients</Card.Title>
                        <ul>
                          {foodDetails.ingredients.map((ing, idx) => (
                            <li key={idx}>{ing}</li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>
            )}

            {/* Preparation */}
            {foodDetails.preparation && (
              <Card className="border-0 mb-4">
                <Card.Body>
                  <Card.Title>Preparation</Card.Title>
                  <div
                    className="text-secondary-emphasis overflow-auto"
                    style={{ maxHeight: "400px", paddingRight: "10px" }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizedPrep || "<p>No preparation details.</p>",
                    }}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Notes */}
            {foodDetails.notes && (
              <Card className="border-0 mb-4">
                <Card.Body>
                  <Card.Title>Notes</Card.Title>
                  <Card.Text>{foodDetails.notes}</Card.Text>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Right Sidebar (Ads / Extra Section) */}
          <Col lg={3}>
            <h6 style={{ fontSize: "11px" }}>ADVERTISEMENT</h6>
            <img
              src="https://s0.2mdn.net/simgad/6463644956967963792"
              alt="Advertisement"
              height={"250px"}
              width={"300px"}
              className="mb-2"
            />
            <img
              src="https://s0.2mdn.net/simgad/6463644956967963792"
              alt="Advertisement"
              height={"250px"}
              width={"300px"}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default FoodDetails;
